import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import type { SxProps } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type { CreditSourceType } from "@polifonia/interlude/billingConstants";
import {
  FEATURE_DISPLAY_NAMES,
  PRODUCT_DISPLAY_NAMES,
} from "@polifonia/interlude/billingConstants";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import dayjs from "dayjs";
import { groupBy, orderBy } from "es-toolkit";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EMPTY_STRING_PLACEHOLDER } from "@/appConstant";
import { MotifIcon } from "@/components/MotifIcon";
import { NumberFormat } from "@/components/NumberFormat";
import { Typography } from "@/components/Typography";
import { useDateFormat } from "@/hooks/useDateFormat";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { TransactionExpandedRow } from "@/pages/billing/v2/tabs/transactionHistory/TransactionExpandedRow";
import type { TransactionFilters } from "@/pages/billing/v2/tabs/transactionHistory/utils";
import {
  getSourceTypeLabels,
  TRANSACTION_TABLE_COLUMN_COUNT,
} from "@/pages/billing/v2/tabs/transactionHistory/utils";

const styles = {
  table: {
    tableLayout: "fixed",
  },
  headerCell: {
    color: theme.colors.neutral050,
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    borderBottom: `1px solid ${theme.colors.neutral020}`,
  },
  dateGroupRow: {
    cursor: "pointer",
    backgroundColor: theme.colors.neutral010,
    "&:hover": {
      backgroundColor: theme.colors.neutral020,
    },
  },
  dateGroupCell: {
    borderBottom: `1px solid ${theme.colors.neutral020}`,
    verticalAlign: "middle",
    py: "10px",
  },
  dateGroupLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  bodyRow: {
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
  },
  bodyCell: {
    borderBottom: `1px solid ${theme.colors.neutral010}`,
    verticalAlign: "middle",
  },
  expandCell: {
    borderBottom: `1px solid ${theme.colors.neutral010}`,
    verticalAlign: "middle",
    textAlign: "right",
    width: "48px",
  },
  errorCell: {
    textAlign: "center",
    py: "48px",
  },
  loadingCell: {
    textAlign: "center",
    py: "48px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const DATE_GROUP_HEADER_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
};

const getDateKey = (createdAt: string | null): string => {
  if (!createdAt) return "unknown";
  return dayjs(createdAt).format("YYYY-MM-DD");
};

const getSourceTypeDisplay = (
  item: InterludeTypes["BalanceTransactionItem"],
  sourceTypeLabels: Record<CreditSourceType, string>,
): string => {
  const label = item.sourceType
    ? sourceTypeLabels[item.sourceType]
    : EMPTY_STRING_PLACEHOLDER;
  if (item.settlementItem?.product && item.settlementItem?.feature) {
    return `${label} - ${PRODUCT_DISPLAY_NAMES[item.settlementItem.product]} / ${FEATURE_DISPLAY_NAMES[item.settlementItem.feature]}`;
  }
  return label;
};

interface TransactionRowProps {
  item: InterludeTypes["BalanceTransactionItem"];
  isExpanded: boolean;
  onToggle: () => void;
  sourceTypeLabels: Record<CreditSourceType, string>;
}

const TransactionRow: React.FC<TransactionRowProps> = function TransactionRow({
  item,
  isExpanded,
  onToggle,
  sourceTypeLabels,
}) {
  const hasSettlement = item.settlementItem !== null;
  const isPositive = item.amount >= 0;

  return (
    <Fragment>
      <TableRow
        sx={hasSettlement ? styles.clickableRow : styles.bodyRow}
        onClick={hasSettlement ? onToggle : undefined}
      >
        <TableCell sx={styles.bodyCell}>
          <Typography variant="body">
            {getSourceTypeDisplay(item, sourceTypeLabels)}
          </Typography>
        </TableCell>
        <TableCell sx={styles.bodyCell}>
          <Typography
            variant="body"
            color={isPositive ? theme.colors.green060 : theme.colors.red060}
          >
            {isPositive ? "+" : ""}
            <NumberFormat
              numberFormatPreset="currency"
              numberFormatOptions={{ currency: item.currency }}
              value={item.amount}
            />
          </Typography>
        </TableCell>
        <TableCell sx={styles.bodyCell}>
          <NumberFormat
            numberFormatPreset="currency"
            numberFormatOptions={{ currency: item.currency }}
            value={item.balanceAfter}
          />
        </TableCell>
        <TableCell sx={styles.expandCell}>
          {hasSettlement ? (
            <IconButton size="small">
              {isExpanded ? (
                <MotifIcon un-i-motif="arrow_up" />
              ) : (
                <MotifIcon un-i-motif="arrow_down" />
              )}
            </IconButton>
          ) : null}
        </TableCell>
      </TableRow>
      {hasSettlement ? (
        <TransactionExpandedRow item={item} open={isExpanded} />
      ) : null}
    </Fragment>
  );
};

interface TransactionTableProps {
  filters: TransactionFilters;
}

export const TransactionTable: React.FC<TransactionTableProps> =
  function TransactionTable({ filters }) {
    const { t } = useTranslation();
    const dateGroupFormat = useDateFormat({
      dateFormatOptions: DATE_GROUP_HEADER_FORMAT,
    });

    const [expandedRows, setExpandedRows] = useState<Set<number>>(
      () => new Set(),
    );
    const [expandedDates, setExpandedDates] = useState<Set<string>>(
      () => new Set(),
    );

    const transactionsQuery = interlude.balance.useGetBalanceTransactions({
      queries: {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      },
    });

    const sourceTypeLabels = getSourceTypeLabels(t);

    const dateGroups = useMemo(() => {
      const items = transactionsQuery.data?.items ?? [];
      const sorted = orderBy(items, [(i) => i.createdAt ?? ""], ["desc"]);
      const grouped = groupBy(sorted, (i) => getDateKey(i.createdAt));
      return Object.entries(grouped);
    }, [transactionsQuery.data?.items]);

    const handleToggleDateGroup = useHandler((dateKey: string) => {
      setExpandedDates((prev) => {
        const next = new Set(prev);
        if (next.has(dateKey)) {
          next.delete(dateKey);
        } else {
          next.add(dateKey);
        }
        return next;
      });
    });

    const handleToggleRow = useHandler((id: number) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    });

    return (
      <Box>
        <TableContainer>
          <Table sx={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.headerCell}>
                  {t("billing.transaction.table.sourceType")}
                </TableCell>
                <TableCell sx={styles.headerCell}>
                  {t("billing.transaction.table.amount")}
                </TableCell>
                <TableCell sx={styles.headerCell}>
                  {t("billing.transaction.table.balanceAfter")}
                </TableCell>
                <TableCell sx={styles.headerCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {transactionsQuery.isPending ? (
                <TableRow>
                  <TableCell
                    colSpan={TRANSACTION_TABLE_COLUMN_COUNT}
                    sx={styles.loadingCell}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : transactionsQuery.isError ? (
                <TableRow>
                  <TableCell
                    colSpan={TRANSACTION_TABLE_COLUMN_COUNT}
                    sx={styles.errorCell}
                  >
                    <Typography variant="body">
                      {t("billing.error.generic")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                dateGroups.map(([dateKey, items]) => {
                  const isCollapsed = !expandedDates.has(dateKey);

                  return (
                    <Fragment key={dateKey}>
                      <TableRow
                        sx={styles.dateGroupRow}
                        onClick={() => handleToggleDateGroup(dateKey)}
                      >
                        <TableCell
                          colSpan={TRANSACTION_TABLE_COLUMN_COUNT}
                          sx={styles.dateGroupCell}
                        >
                          <Box sx={styles.dateGroupLeft}>
                            <IconButton size="small">
                              {isCollapsed ? (
                                <MotifIcon un-i-motif="arrow_down" />
                              ) : (
                                <MotifIcon un-i-motif="arrow_up" />
                              )}
                            </IconButton>
                            <Typography variant="body" fontWeight="medium">
                              {dateKey === "unknown"
                                ? "Unknown Date"
                                : dateGroupFormat(dateKey)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {!isCollapsed &&
                        items.map((item) => (
                          <TransactionRow
                            key={item.id}
                            item={item}
                            isExpanded={expandedRows.has(item.id)}
                            onToggle={() => handleToggleRow(item.id)}
                            sourceTypeLabels={sourceTypeLabels}
                          />
                        ))}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
